import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Card,
  CardContent,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import {
  ArrowLeft,
  Loader2,
  Calendar,
  Clock,
  BookOpen,
  Target,
  RotateCcw,
  CheckCircle,
  SkipForward,
  Pause,
  Play,
  XCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react"
import {
  getPrepPlan,
  generatePrepDailyTasks,
  updatePrepProgress,
  pausePrepPlan,
  resumePrepPlan,
  abandonPrepPlan,
  type PrepPlanDetail,
  type PrepDailyTaskItem,
  type WeeklyPlanItem,
} from "@/api/examPrep"
import { ROUTES } from "@/constants/routes"

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: "bg-green-100", text: "text-green-700", label: "进行中" },
  completed: { bg: "bg-blue-100", text: "text-blue-700", label: "已完成" },
  paused: { bg: "bg-gray-100", text: "text-gray-600", label: "已暂停" },
  abandoned: { bg: "bg-red-100", text: "text-red-700", label: "已放弃" },
}

const WEEK_STATUS_ICONS: Record<string, typeof CheckCircle> = {
  completed: CheckCircle,
  in_progress: Target,
  upcoming: Clock,
}

const WEEK_STATUS_COLORS: Record<string, string> = {
  completed: "text-green-600",
  in_progress: "text-primary",
  upcoming: "text-slate-400",
}

const TASK_TYPE_ICONS: Record<string, typeof BookOpen> = {
  lesson: BookOpen,
  practice: Target,
  retry: RotateCcw,
  review: BookOpen,
}

const TASK_TYPE_LABELS: Record<string, string> = {
  lesson: "学习",
  practice: "练习",
  retry: "重做错题",
  review: "复习",
}

const TASK_TYPE_COLORS: Record<string, string> = {
  lesson: "bg-blue-100 text-blue-600",
  practice: "bg-green-100 text-green-600",
  retry: "bg-orange-100 text-orange-600",
  review: "bg-purple-100 text-purple-600",
}

function getDaysRemaining(examDate: string): number | null {
  if (!examDate) return null
  const exam = new Date(examDate)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  exam.setHours(0, 0, 0, 0)
  const diff = Math.ceil((exam.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  return diff >= 0 ? diff : null
}

export default function PlanDetailPage() {
  const { planId } = useParams<{ planId: string }>()
  const navigate = useNavigate()

  const [plan, setPlan] = useState<PrepPlanDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Daily tasks state
  const [date, setDate] = useState(new Date().toISOString().split("T")[0])
  const [tasks, setTasks] = useState<PrepDailyTaskItem[]>([])
  const [generating, setGenerating] = useState(false)

  // Weekly plan expand state
  const [expandedWeek, setExpandedWeek] = useState<number | null>(null)

  // Action loading states
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    if (planId) loadPlan()
  }, [planId])

  async function loadPlan() {
    if (!planId) return
    setLoading(true)
    setError(null)
    try {
      const data = await getPrepPlan(planId)
      setPlan(data)
    } catch (err) {
      console.error("Failed to load plan:", err)
      setError("加载计划详情失败")
    } finally {
      setLoading(false)
    }
  }

  async function handleGenerateTasks() {
    if (!planId) return
    setGenerating(true)
    try {
      const data = await generatePrepDailyTasks(planId, date)
      setTasks(data.tasks ?? [])
    } catch (err) {
      console.error("Failed to generate tasks:", err)
    } finally {
      setGenerating(false)
    }
  }

  async function handleTaskComplete(taskCompleted: boolean) {
    if (!planId) return
    try {
      await updatePrepProgress(planId, { taskCompleted })
      // Refresh plan to get updated progress
      const data = await getPrepPlan(planId)
      setPlan(data)
    } catch (err) {
      console.error("Failed to update progress:", err)
    }
  }

  async function handlePause() {
    if (!planId) return
    setActionLoading(true)
    try {
      await pausePrepPlan(planId)
      await loadPlan()
    } catch (err) {
      console.error("Failed to pause:", err)
    } finally {
      setActionLoading(false)
    }
  }

  async function handleResume() {
    if (!planId) return
    setActionLoading(true)
    try {
      await resumePrepPlan(planId)
      await loadPlan()
    } catch (err) {
      console.error("Failed to resume:", err)
    } finally {
      setActionLoading(false)
    }
  }

  async function handleAbandon() {
    if (!planId) return
    if (!confirm("确定要放弃这个备考计划吗？此操作不可撤销。")) return
    setActionLoading(true)
    try {
      await abandonPrepPlan(planId)
      navigate(ROUTES.EXAM_PREP)
    } catch (err) {
      console.error("Failed to abandon:", err)
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error || !plan) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <p className="text-red-500 mb-4">{error ?? "计划不存在"}</p>
              <Button onClick={() => navigate(ROUTES.EXAM_PREP)}>返回列表</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const statusStyle = STATUS_STYLES[plan.status] ?? STATUS_STYLES.active
  const daysRemaining = getDaysRemaining(plan.exam_date)
  const progressPct =
    plan.total_weeks > 0
      ? Math.round((plan.current_week / plan.total_weeks) * 100)
      : 0

  const completedTaskCount = tasks.filter((t) => t.status === "done").length
  const totalTaskDuration = tasks.reduce((sum, t) => sum + t.duration, 0)
  const completedTaskDuration = tasks
    .filter((t) => t.status === "done")
    .reduce((sum, t) => sum + t.duration, 0)

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate(ROUTES.EXAM_PREP)}
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        返回列表
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-3xl font-bold text-slate-800">{plan.exam_name}</h1>
            <span
              className={`text-xs px-2 py-0.5 rounded ${statusStyle.bg} ${statusStyle.text}`}
            >
              {statusStyle.label}
            </span>
          </div>
          <p className="text-slate-500">
            {plan.subject} · {plan.total_weeks} 周计划
          </p>
        </div>
        <div className="flex items-center gap-2">
          {plan.status === "active" && (
            <Button variant="outline" onClick={handlePause} disabled={actionLoading}>
              <Pause className="w-4 h-4 mr-1" />
              暂停
            </Button>
          )}
          {plan.status === "paused" && (
            <Button variant="outline" onClick={handleResume} disabled={actionLoading}>
              <Play className="w-4 h-4 mr-1" />
              继续
            </Button>
          )}
          {(plan.status === "active" || plan.status === "paused") && (
            <Button variant="destructive" onClick={handleAbandon} disabled={actionLoading}>
              <XCircle className="w-4 h-4 mr-1" />
              放弃
            </Button>
          )}
        </div>
      </div>

      {/* Countdown + Progress Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6 text-center">
            <Calendar className="w-6 h-6 text-orange-500 mx-auto mb-2" />
            {daysRemaining !== null ? (
              <>
                <div className="text-3xl font-bold text-orange-600">{daysRemaining}</div>
                <p className="text-sm text-slate-500">距离考试还剩（天）</p>
              </>
            ) : (
              <>
                <div className="text-lg font-medium text-slate-400">未设置</div>
                <p className="text-sm text-slate-500">考试日期待定</p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-slate-700">整体进度</span>
            </div>
            <Progress value={progressPct} className="h-3 mb-2" />
            <p className="text-sm text-slate-500">
              第 {plan.current_week} / {plan.total_weeks} 周
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-green-600">{plan.completed_days}</div>
            <p className="text-sm text-slate-500">已完成学习天数</p>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Plan Timeline */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-slate-700 mb-4">周计划</h2>
        <div className="space-y-2">
          {plan.weekly_plan.map((week: WeeklyPlanItem) => {
            const StatusIcon = WEEK_STATUS_ICONS[week.status] ?? Clock
            const iconColor = WEEK_STATUS_COLORS[week.status] ?? "text-slate-400"
            const isExpanded = expandedWeek === week.week
            const isInProgress = week.status === "in_progress"

            return (
              <Card
                key={week.week}
                className={isInProgress ? "ring-2 ring-primary shadow-sm" : ""}
              >
                <CardContent className="py-0">
                  <div
                    className="flex items-center gap-3 py-3 cursor-pointer"
                    onClick={() => setExpandedWeek(isExpanded ? null : week.week)}
                  >
                    <StatusIcon className={`w-5 h-5 ${iconColor}`} />
                    <div className="flex-grow">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">
                          第 {week.week} 周
                        </span>
                        <span className="text-slate-600">· {week.theme}</span>
                        {isInProgress && (
                          <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                            当前
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">
                      {week.dailyMinutes} 分钟/天
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                  </div>

                  {isExpanded && (
                    <div className="pb-4 pt-1 border-t border-slate-100">
                      {week.goals.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-slate-500 mb-1">本周目标</p>
                          <ul className="space-y-1">
                            {week.goals.map((goal, i) => (
                              <li key={i} className="text-sm text-slate-600 flex items-start gap-1.5">
                                <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-500 flex-shrink-0" />
                                {goal}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {week.knowledgePoints.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-1">知识点</p>
                          <div className="flex flex-wrap gap-1.5">
                            {week.knowledgePoints.map((kp, i) => (
                              <span
                                key={i}
                                className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded"
                              >
                                {kp}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>

      {/* Daily Tasks Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-slate-700">每日任务</h2>
          <Button onClick={handleGenerateTasks} disabled={generating}>
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                生成中...
              </>
            ) : (
              "生成今日任务"
            )}
          </Button>
        </div>

        {/* Date Selector */}
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const d = new Date(date)
              d.setDate(d.getDate() - 1)
              setDate(d.toISOString().split("T")[0])
            }}
          >
            ← 前一天
          </Button>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="font-medium">{date}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const d = new Date(date)
              d.setDate(d.getDate() + 1)
              setDate(d.toISOString().split("T")[0])
            }}
          >
            后一天 →
          </Button>
        </div>

        {tasks.length > 0 ? (
          <>
            {/* Task Summary */}
            <Card className="mb-4">
              <CardContent className="pt-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {completedTaskCount}/{tasks.length}
                    </div>
                    <p className="text-sm text-slate-500">已完成任务</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {completedTaskDuration}
                    </div>
                    <p className="text-sm text-slate-500">已学习(分钟)</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-600">
                      {totalTaskDuration}
                    </div>
                    <p className="text-sm text-slate-500">总计(分钟)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Task List */}
            <div className="space-y-3">
              {tasks.map((task, idx) => {
                const Icon = TASK_TYPE_ICONS[task.type] ?? BookOpen
                return (
                  <Card
                    key={idx}
                    className={`transition-all ${
                      task.status === "done" ? "opacity-60" : ""
                    }`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            TASK_TYPE_COLORS[task.type] ?? TASK_TYPE_COLORS.lesson
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>

                        <div className="flex-grow">
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-xs px-2 py-0.5 rounded ${
                                TASK_TYPE_COLORS[task.type] ?? TASK_TYPE_COLORS.lesson
                              }`}
                            >
                              {TASK_TYPE_LABELS[task.type] ?? task.type}
                            </span>
                            <span
                              className={
                                task.status === "done"
                                  ? "line-through text-slate-400"
                                  : "font-medium"
                              }
                            >
                              {task.title}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                            <Clock className="w-3 h-3" />
                            <span>{task.duration}分钟</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {task.status === "pending" ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleTaskComplete(true)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                完成
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleTaskComplete(false)}
                              >
                                <SkipForward className="w-4 h-4" />
                              </Button>
                            </>
                          ) : task.status === "done" ? (
                            <span className="text-sm text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              已完成
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400">已跳过</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">
                  还没有今日任务
                </h3>
                <p className="text-slate-500 mb-6">
                  点击"生成今日任务"按钮，AI 将为你安排今天的学习计划
                </p>
                <Button onClick={handleGenerateTasks} disabled={generating}>
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      生成中...
                    </>
                  ) : (
                    "生成今日任务"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
